// Precondition: None
// Postcondition: Custom element for displaying chat messages
// Invariants: from="user"|"assistant" determines styling

import { icon } from "../../../shared/icons.js";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { showToast } from "../toast/toast.js";
import type {
  MessageRole,
  MessagePart,
  TextPart,
  ToolPart,
} from "../../types/chat.js";

// Configure marked for chat messages
marked.setOptions({
  gfm: true,
  breaks: true,
});

if (!customElements.get("ft-chat-message")) {
  class FtChatMessage extends HTMLElement {
    private partsContainer: HTMLElement | null = null;
    private thinkingDots: HTMLElement | null = null;
    private copyBtn: HTMLButtonElement | null = null;
    private _streaming = false;
    private _parts: MessagePart[] = [];
    private _version = 0;
    private _lastRenderedVersion = -1;
    private partCache = new Map<number, HTMLElement>();

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }

    static get observedAttributes(): string[] {
      return ["from", "streaming"];
    }

    get from(): MessageRole {
      return (this.getAttribute("from") as MessageRole) || "user";
    }

    set from(value: MessageRole) {
      this.setAttribute("from", value);
    }

    get streaming(): boolean {
      return this._streaming;
    }

    set streaming(value: boolean) {
      this._streaming = value;
      if (value) {
        this.setAttribute("streaming", "");
      } else {
        this.removeAttribute("streaming");
      }
    }

    get parts(): MessagePart[] {
      return this._parts;
    }

    /**
     * Set message parts and trigger render.
     * Version check prevents duplicate renders when parent sets parts
     * multiple times per frame during SSE streaming.
     */
    set parts(value: MessagePart[]) {
      if (this._version === this._lastRenderedVersion) return;
      this._lastRenderedVersion = this._version;
      this._parts = value;
      this.renderParts();
    }

    set version(value: number) {
      this._version = value;
    }

    /** Reset message state for reuse. */
    clear(): void {
      this._parts = [];
      this._version = 0;
      this._lastRenderedVersion = -1;
      this.partCache.clear();
      if (this.partsContainer) {
        this.partsContainer.innerHTML = "";
      }
    }

    connectedCallback(): void {
      this.render();
      this.setupReferences();
    }

    attributeChangedCallback(
      name: string,
      oldValue: string | null,
      newValue: string | null,
    ): void {
      if (name === "streaming") {
        this._streaming = newValue !== null;
      }
    }

    private render(): void {
      const style = document.createElement("style");
      style.textContent = this.getStyles();

      const template = document.createElement("template");
      template.innerHTML = this.getTemplate();

      if (this.shadowRoot) {
        this.shadowRoot.appendChild(style);
        this.shadowRoot.appendChild(template.content.cloneNode(true));
      }
    }

    private getStyles(): string {
      return `
        :host {
          display: block;
          margin-bottom: 32px;
        }

        /* User message: dark bubble, no avatar, right-aligned */
        :host([from="user"]) .message {
          display: flex;
          justify-content: flex-end;
        }

        :host([from="user"]) .bubble {
          background: #1C1C1E;
          color: #ffffff;
          padding: 12px 18px;
          border-radius: 20px;
          border-bottom-right-radius: 6px;
          font-family: var(--font-sans, Inter, system-ui, sans-serif);
          font-size: 15px;
          line-height: 1.5;
        }

        /* Assistant message: flowing text, full width for stability during streaming */
        :host([from="assistant"]) .message {
          display: flex;
        }

        :host([from="assistant"]) .content {
          flex: 1;
          min-width: 0;
        }

        :host([from="assistant"]) .bubble {
          font-family: var(--font-sans, Inter, system-ui, sans-serif);
          font-size: 15px;
          line-height: 1.7;
          color: var(--text, #1f2937);
        }

        .content {
          display: flex;
          flex-direction: column;
        }

        /* Parts container */
        .parts-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        /* Action result confirmation */
        .action-result {
          padding: 8px 12px;
          background: var(--success-bg, rgba(34, 197, 94, 0.1));
          border-left: 3px solid var(--success, #22c55e);
          border-radius: 4px;
          font-size: 13px;
          color: var(--text, #1f2937);
        }

        /* Streaming indicator: animated bouncing dots */
        .thinking-dots {
          display: none;
          align-items: center;
          gap: 3px;
          padding: 4px 0;
        }

        :host([streaming]) .thinking-dots {
          display: flex;
        }

        :host([streaming]) .thinking-dots.hidden {
          display: none;
        }

        .thinking-dots span {
          width: 4px;
          height: 4px;
          background: var(--muted, #6b7280);
          border-radius: 50%;
          animation: bounce 1.4s infinite ease-in-out;
        }

        .thinking-dots span:nth-child(1) { animation-delay: -0.32s; }
        .thinking-dots span:nth-child(2) { animation-delay: -0.16s; }
        .thinking-dots span:nth-child(3) { animation-delay: 0s; }

        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-4px); }
        }

        /* Markdown rendering */
        .text-part p {
          margin: 0 0 16px;
        }

        .text-part p:last-child {
          margin-bottom: 0;
        }

        .text-part strong {
          font-weight: 600;
          color: var(--text, #1f2937);
        }

        .text-part em {
          font-style: italic;
        }

        .text-part code {
          background: var(--tab-bg, #f3f4f6);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 13px;
        }

        :host([from="user"]) .text-part code {
          background: rgba(255, 255, 255, 0.2);
        }

        .text-part pre {
          background: var(--panel, #1a1c22);
          color: var(--text, #f3f4f6);
          padding: 16px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 16px 0;
          font-size: 13px;
          line-height: 1.5;
        }

        .text-part pre code {
          background: transparent;
          padding: 0;
        }

        .text-part ul, .text-part ol {
          margin: 12px 0;
          padding-left: 24px;
        }

        .text-part ul {
          list-style-type: disc;
        }

        .text-part ol {
          list-style-type: decimal;
        }

        .text-part li {
          margin-bottom: 8px;
          padding-left: 6px;
          line-height: 1.6;
        }

        .text-part li::marker {
          color: var(--muted, #6b7280);
        }

        .text-part li:last-child {
          margin-bottom: 0;
        }

        /* Nested lists */
        .text-part li ul, .text-part li ol {
          margin: 8px 0 0;
        }

        .text-part h1, .text-part h2, .text-part h3 {
          font-weight: 600;
          letter-spacing: -0.01em;
          margin: 20px 0 12px;
          color: var(--text, #1f2937);
        }

        .text-part h1:first-child,
        .text-part h2:first-child,
        .text-part h3:first-child {
          margin-top: 0;
        }

        .text-part h1 { font-size: 18px; }
        .text-part h2 { font-size: 16px; }
        .text-part h3 { font-size: 15px; }

        /* Tables - styled like ft-holdings-table */
        .text-part table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
          font-size: 14px;
          border: 1px solid var(--divider-color, #e2e4e8);
          border-radius: 8px;
          overflow: hidden;
        }

        .text-part thead th {
          text-align: left;
          padding: 10px 12px;
          background: var(--surface, #f4f4f5);
          border-bottom: 1px solid var(--divider-color, #e2e4e8);
          font-weight: 600;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--muted, #555555);
        }

        .text-part tbody td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--divider-color, #e2e4e8);
          color: var(--text, #1f2937);
        }

        .text-part tbody tr:last-child td {
          border-bottom: none;
        }

        .text-part tbody tr:hover td {
          background: var(--surface, #f4f4f5);
        }

        /* Message actions - hidden by default, show on hover */
        .actions {
          display: flex;
          gap: 4px;
          margin-top: 8px;
          opacity: 0;
          transition: opacity 0.15s ease;
        }

        .message:hover .actions,
        .actions:focus-within {
          opacity: 1;
        }

        .action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          padding: 0;
          background: transparent;
          border: 1px solid var(--card-border, #e5e7eb);
          border-radius: 6px;
          cursor: pointer;
          color: var(--muted, #6b7280);
          transition: all 0.15s ease;
        }

        .action-btn:hover {
          background: var(--surface, #f3f4f6);
          color: var(--text, #1f2937);
        }

        .action-btn svg {
          width: 14px;
          height: 14px;
        }

        .action-btn.copied {
          color: var(--success, #22c55e);
          border-color: var(--success, #22c55e);
        }

        :host([from="user"]) .actions {
          justify-content: flex-end;
        }

        @media (prefers-color-scheme: dark) {
          :host([from="user"]) .bubble {
            background: #24262c;
          }

          :host([from="assistant"]) .bubble {
            color: var(--text, #f3f4f6);
          }

          .text-part strong {
            color: var(--text, #f3f4f6);
          }

          .text-part h1, .text-part h2, .text-part h3 {
            color: var(--heading, #ffffff);
          }

          .text-part code {
            background: var(--tab-bg, #1a1c22);
          }

          .text-part pre {
            background: var(--panel, #0f1014);
            color: var(--text, #e5e7eb);
          }

          .text-part table {
            border-color: var(--divider-color, #2a2d33);
          }

          .text-part thead th {
            background: var(--surface, #14161a);
            border-bottom-color: var(--divider-color, #2a2d33);
            color: var(--muted, #a3a3a3);
          }

          .text-part tbody td {
            border-bottom-color: var(--divider-color, #2a2d33);
            color: var(--text, #f3f4f6);
          }

          .text-part tbody tr:hover td {
            background: var(--surface, #14161a);
          }

          .action-btn {
            border-color: var(--card-border, #24262c);
          }

          .action-btn:hover {
            background: var(--surface, #14161a);
            color: var(--text, #f3f4f6);
          }
        }
      `;
    }

    private getTemplate(): string {
      return `
        <div class="message">
          <div class="content">
            <div class="bubble">
              <div class="parts-container"></div>
              <div class="thinking-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <div class="actions">
              <button class="action-btn copy-btn" type="button" aria-label="Copy message">
                <span class="copy-icon">${icon("copy", 14)}</span>
                <span class="check-icon" style="display: none;">${icon("check", 14)}</span>
              </button>
            </div>
          </div>
        </div>
      `;
    }

    private setupReferences(): void {
      this.partsContainer =
        this.shadowRoot?.querySelector(".parts-container") ?? null;
      this.thinkingDots =
        this.shadowRoot?.querySelector(".thinking-dots") ?? null;
      this.copyBtn = this.shadowRoot?.querySelector(".copy-btn") ?? null;
      this.setupEventListeners();
      // Render initial parts if set before connected
      if (this._parts.length > 0) {
        this.renderParts();
      }
    }

    private setupEventListeners(): void {
      this.copyBtn?.addEventListener("click", () => this.copyMessage());
    }

    private async copyMessage(): Promise<void> {
      if (!this.copyBtn) return;

      // Get text from all text parts
      const rawContent = this._parts
        .filter((p): p is TextPart => p.type === "text")
        .map((p) => p.text)
        .join("\n\n");

      try {
        await navigator.clipboard.writeText(rawContent);
        showToast({ message: "Copied to clipboard", type: "success" });

        // Show success state on button
        this.copyBtn.classList.add("copied");
        const copyIcon = this.copyBtn.querySelector(
          ".copy-icon",
        ) as HTMLElement;
        const checkIcon = this.copyBtn.querySelector(
          ".check-icon",
        ) as HTMLElement;

        if (copyIcon) copyIcon.style.display = "none";
        if (checkIcon) checkIcon.style.display = "block";

        // Reset after 2 seconds
        setTimeout(() => {
          this.copyBtn?.classList.remove("copied");
          if (copyIcon) copyIcon.style.display = "block";
          if (checkIcon) checkIcon.style.display = "none";
        }, 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
        showToast({ message: "Copy failed. Try again.", type: "error" });
      }
    }

    private renderMarkdown(text: string): string {
      const html = marked.parse(text, { async: false }) as string;
      return DOMPurify.sanitize(html);
    }

    /**
     * Render message parts using node cache pattern.
     *
     * Uses Map<index, Element> for O(1) DOM access without querySelector.
     * Text parts update in-place; tool parts are immutable once created.
     *
     * Precondition: partsContainer exists in shadow DOM
     * Postcondition: DOM reflects _parts array state
     * Invariant: partCache[i] === partsContainer.children[i]
     */
    private renderParts(): void {
      if (!this.partsContainer) return;

      // Hide thinking dots once there's any text content
      const hasTextContent = this._parts.some(
        (p) => p.type === "text" && p.text.length > 0,
      );
      if (this.thinkingDots) {
        this.thinkingDots.classList.toggle("hidden", hasTextContent);
      }

      this._parts.forEach((part, index) => {
        let el = this.partCache.get(index);

        if (part.type === "text") {
          const content =
            this.from === "assistant"
              ? this.renderMarkdown(part.text)
              : this.escapeHtml(part.text);

          if (el) {
            // Update existing text element in-place (streaming)
            el.innerHTML = content;
            el.classList.toggle("streaming", part.state === "streaming");
          } else {
            // Create new text element and cache it
            el = document.createElement("div");
            el.className = `text-part${part.state === "streaming" ? " streaming" : ""}`;
            el.dataset.partIndex = String(index);
            el.innerHTML = content;
            this.partsContainer!.appendChild(el);
            this.partCache.set(index, el);
          }
        } else {
          // Tool part: placeholder may need upgrade to final element
          if (el) {
            if (el.tagName === "FT-CHAT-TOOL-RESULT") {
              const result = part.result as
                | { component?: string; model?: string }
                | undefined;
              // Replace placeholder with component/action when result arrives
              if (
                part.status === "success" &&
                (result?.component || result?.model)
              ) {
                try {
                  const newEl = this.createToolElement(part);
                  newEl.dataset.partIndex = String(index);
                  el.replaceWith(newEl);
                  this.partCache.set(index, newEl);
                } catch (err) {
                  console.error("Failed to create tool element:", err);
                  // Keep the JSON card as fallback
                }
                return;
              }
              // Update data for JSON card (status/result changes)
              (el as HTMLElement & { data: unknown }).data = {
                name: part.name,
                status: part.status,
                result: part.result,
                error: part.error,
              };
            }
            // Component/action tools are immutable after creation
            return;
          }

          try {
            el = this.createToolElement(part);
            el.dataset.partIndex = String(index);
            this.partsContainer!.appendChild(el);
            this.partCache.set(index, el);
          } catch (err) {
            console.error("Failed to create tool element:", err);
            // Create error fallback element
            el = document.createElement("div");
            el.className = "action-result";
            el.style.borderLeftColor = "var(--error, #ef4444)";
            el.textContent = "Failed to render tool result";
            el.dataset.partIndex = String(index);
            this.partsContainer!.appendChild(el);
            this.partCache.set(index, el);
          }
        }
      });
    }

    private createToolElement(part: ToolPart): HTMLElement {
      // Component instruction
      const result = part.result as
        | { component?: string; attributes?: Record<string, string> }
        | undefined;
      if (
        part.status === "success" &&
        result?.component &&
        result?.attributes
      ) {
        const el = document.createElement(result.component);
        el.setAttribute("data-tool-id", part.id);
        for (const [key, value] of Object.entries(result.attributes)) {
          el.setAttribute(key, String(value));
        }
        return el;
      }

      // Default: JSON card
      const el = document.createElement("ft-chat-tool-result");
      el.setAttribute("data-tool-id", part.id);
      (
        el as HTMLElement & {
          data: {
            name: string;
            status: string;
            result?: unknown;
            error?: string;
          };
        }
      ).data = {
        name: part.name,
        status: part.status,
        result: part.result,
        error: part.error,
      };
      return el;
    }

    private escapeHtml(text: string): string {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    }
  }

  customElements.define("ft-chat-message", FtChatMessage);
}
